// Shared media-type classification for timeline tracks. Used by addBoxThunk
// (default shared-row placement) and by the timeline's getLayerType so the two
// can never diverge.

// Check order is load-bearing and mirrors getLayerType exactly: "TextPlayer"
// contains "player", so text|title must be classified BEFORE video|player.
// Texts share a row like media (caption-style sequential use); shapes, smart/
// interactive elements and full-scene widgets deliberately stay solo.
export function classifyPluginTrackType(pluginName) {
  if (!pluginName || typeof pluginName !== "string") return null;
  const p = pluginName.toLowerCase();
  if (p === "scene-audio") return null;
  if (p.includes("image") || p.includes("photo") || p.includes("hotspot")) return "image";
  if (p.includes("text") || p.includes("title")) return "text";
  if (p.includes("video") || p.includes("player")) return "video";
  if (p.includes("audio") || p.includes("sound") || p.includes("audiocue")) return "audio";
  return null;
}

export function sharedTrackIdForType(type) {
  return `type:${type}`;
}

export const DEFAULT_IMAGE_DURATION_FRAMES = 150; // 5s @ 30fps

// Is the span [startFrame, endFrame) free of every member of `trackId` within
// one scene? Decides whether a new element can slot into the shared row at
// the playhead or must open its own row. Members whose endFrame hasn't been
// persisted yet (media probe still pending) count as start + fallback.
export function isTrackSpanFree(
  boxesById = {},
  sceneBoxIds = [],
  trackId,
  startFrame,
  endFrame,
  fallbackLenFrames = DEFAULT_IMAGE_DURATION_FRAMES
) {
  if (!trackId || !Array.isArray(sceneBoxIds)) return true;
  return !sceneBoxIds.some((id) => {
    const tl = boxesById[id] && boxesById[id].timeline;
    if (!tl || tl.trackId !== trackId) return false;
    const mStart = typeof tl.startFrame === "number" ? tl.startFrame : 0;
    const mEnd = typeof tl.endFrame === "number" ? tl.endFrame : mStart + fallbackLenFrames;
    return mStart < endFrame && mEnd > startFrame;
  });
}

// A fresh row id for an adoption — mirrors the timeline's own generator
// (IdeaTimeline/index.jsx, clip dropped on an untracked row).
function newTrackId() {
  return "tr-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// Picks a row for the span [start, end) among `rows` — the shared core of the
// box (frames) and narration (seconds) placement policies. A row is
// { trackId, memberIds, members: [{ start, end, known }] }; `trackId: null`
// marks an ungrouped element that can still be ADOPTED into a row.
//
// Order of preference: the type's own row, then any other row with room. A row
// holding a member of unknown length is never joined — the risk of an overlap
// is not worth it (the length lands a moment later, from the media probe).
//
// Returns { trackId, adoptIds, row }: `trackId` is null when nothing has room
// (the element opens its own row), and `adoptIds` lists the existing elements
// that must be stamped with `trackId` for the merge to happen.
export function pickRowForSpan(rows, canonical, start, end) {
  const hasRoom = (row) =>
    row.members.every((m) => m.known !== false) &&
    !row.members.some((m) => m.start < end && m.end > start);

  const canonicalRow = rows.find((row) => row.trackId === canonical);
  if (canonicalRow && hasRoom(canonicalRow)) {
    return { trackId: canonical, adoptIds: [], row: canonicalRow };
  }

  const target = rows.find((row) => row !== canonicalRow && hasRoom(row));
  if (target) {
    // A real row keeps its id; an ungrouped element is adopted into one — the
    // type row when that name is still free, else a row of its own.
    if (target.trackId) return { trackId: target.trackId, adoptIds: [], row: target };
    return {
      trackId: canonicalRow ? newTrackId() : canonical,
      adoptIds: target.memberIds,
      row: target,
    };
  }

  // Nothing has room: open a standalone row — but claim the type row when it
  // does not exist yet, so later adds have something to append to.
  return { trackId: canonicalRow ? null : canonical, adoptIds: [], row: null };
}

// Picks the row a newly-added BOX of `type` should land on for the span
// [startFrame, endFrame). Candidate rows are the type's own track PLUS every
// same-type box that carries no trackId — legacy projects (never migrated) and
// elements that once had to open their own row both look like that, and both
// are still adoptable, so an add lands next to them instead of stacking up yet
// another row.
export function planSharedRowPlacement({
  boxesById = {},
  sceneBoxIds = [],
  pluginIdOf = () => null,
  type,
  startFrame,
  endFrame,
}) {
  const canonical = sharedTrackIdForType(type);
  if (!type || !Array.isArray(sceneBoxIds)) return { trackId: canonical, adoptIds: [] };

  const rows = []; // in scene (bottom-up) order
  const byTrack = new Map();
  sceneBoxIds.forEach((id) => {
    const tl = (boxesById[id] || {}).timeline || {};
    const trackId = typeof tl.trackId === "string" ? tl.trackId : null;
    // A box belongs to this type if it sits on the type's track or if its
    // plugin classifies as it. Rows on a generated track id need the plugin.
    if (trackId !== canonical && classifyPluginTrackType(pluginIdOf(id)) !== type) return;
    const start = typeof tl.startFrame === "number" ? tl.startFrame : 0;
    // The timeline persists a legacy box's displayed end on load, so a missing
    // one here means a media probe is still running.
    const member = { start, end: tl.endFrame, known: typeof tl.endFrame === "number" };
    if (trackId) {
      let row = byTrack.get(trackId);
      if (!row) {
        row = { trackId, memberIds: [], members: [] };
        byTrack.set(trackId, row);
        rows.push(row);
      }
      row.memberIds.push(id);
      row.members.push(member);
    } else if (tl.trackId !== null) {
      // trackId === null is a deliberate solo (clip dragged out of a row) —
      // never adopted. `undefined` is just "not grouped yet".
      rows.push({ trackId: null, memberIds: [id], members: [member] });
    }
  });

  const { trackId, adoptIds } = pickRowForSpan(rows, canonical, startFrame, endFrame);
  return { trackId, adoptIds };
}
